import { motion } from "framer-motion";
import { Brain, Wand2, TrendingUp, LineChart, Cpu, CircuitBoard } from "lucide-react";
import { AI_PILLARS } from "@/lib/constants";

const ICONS = { Brain, Wand2, TrendingUp, LineChart, Cpu };

export default function AIEnhanced() {
    return (
        <section
            id="ai"
            data-testid="ai-section"
            className="relative py-24 md:py-32 bg-ae-ink text-white overflow-hidden"
        >
            {/* Background grid + glow */}
            <div className="absolute inset-0 opacity-[0.08]">
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage:
                            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
                        backgroundSize: "56px 56px",
                    }}
                />
            </div>
            <div className="absolute -left-40 top-40 h-[440px] w-[440px] rounded-full bg-ae-orange/30 blur-[120px]" />
            <div className="absolute -right-40 bottom-20 h-[380px] w-[380px] rounded-full bg-ae-orange-light/20 blur-[110px]" />

            <div className="relative max-w-7xl mx-auto px-6 md:px-10">
                <div className="max-w-3xl">
                    <div className="inline-flex items-center gap-2 text-ae-orange text-xs font-bold uppercase tracking-[0.22em]">
                        <CircuitBoard className="h-4 w-4" />
                        AI-Enhanced in Every Way
                    </div>
                    <h2 className="mt-5 font-display font-extrabold text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-white">
                        From the brief to the buy —{" "}
                        <span className="ae-text-gradient">AI runs through it all</span>.
                    </h2>
                    <p className="mt-6 text-lg md:text-xl text-white/70 leading-relaxed">
                        We don&apos;t bolt AI on at the end. It&apos;s woven into how we plan,
                        create, advertise, measure and build technology — so every brief
                        ships smarter, faster and sharper.
                    </p>
                </div>

                <div className="mt-14 md:mt-20 grid md:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
                    {AI_PILLARS.map((p, i) => {
                        const Icon = ICONS[p.icon];
                        return (
                            <motion.div
                                key={p.title}
                                initial={{ opacity: 0, y: 22 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{
                                    duration: 0.7,
                                    delay: i * 0.08,
                                    ease: [0.22, 1, 0.36, 1],
                                }}
                                data-testid={`ai-pillar-${i}`}
                                className="group relative rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-6 hover:bg-white/[0.07] hover:border-ae-orange/60 transition-all duration-500"
                            >
                                <div className="h-12 w-12 rounded-2xl bg-ae-orange/15 text-ae-orange flex items-center justify-center group-hover:bg-ae-orange group-hover:text-white transition-colors duration-500">
                                    {Icon ? <Icon className="h-6 w-6" /> : null}
                                </div>
                                <h3 className="mt-5 font-display font-bold text-lg text-white leading-tight">
                                    {p.title}
                                </h3>
                                <p className="mt-2 text-sm text-white/65 leading-relaxed">
                                    {p.desc}
                                </p>
                            </motion.div>
                        );
                    })}
                </div>

                {/* Mini KPI strip */}
                <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                        { k: "Faster Briefs", v: "3.4×" },
                        { k: "Creative Variants", v: "12+" },
                        { k: "Avg CPA Drop", v: "−28%" },
                        { k: "Insights → Action", v: "Real-time" },
                    ].map((m) => (
                        <div
                            key={m.k}
                            data-testid={`ai-metric-${m.k.toLowerCase().replace(/\s+/g, "-")}`}
                            className="rounded-2xl border border-white/10 bg-white/[0.02] px-5 py-5"
                        >
                            <div className="font-display font-extrabold text-2xl md:text-3xl text-white">
                                {m.v}
                            </div>
                            <div className="mt-1 text-xs uppercase tracking-widest text-white/50 font-semibold">
                                {m.k}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
